import React, { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from './button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ error, errorInfo })
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  private handleReload = () => {
    window.location.reload()
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <CardTitle className="text-red-900">Something went wrong</CardTitle>
              <CardDescription>
                An unexpected error occurred. Please try refreshing the page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-sm">
                  <p className="font-medium text-red-800 mb-1">Error Details:</p>
                  <p className="text-red-700 font-mono text-xs break-all">
                    {this.state.error.message}
                  </p>
                </div>
              )}
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  onClick={this.handleRetry}
                  className="flex-1"
                >
                  Try Again
                </Button>
                <Button 
                  onClick={this.handleReload}
                  className="flex-1"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reload Page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}

// API Error Display Component
interface ApiErrorProps {
  error: any
  onRetry?: () => void
  className?: string
}

export function ApiError({ error, onRetry, className = '' }: ApiErrorProps) {
  const getErrorMessage = () => {
    if (error?.userMessage) return error.userMessage
    if (error?.serverMessage) return error.serverMessage
    if (error?.message) return error.message
    return 'An unexpected error occurred'
  }

  const getErrorType = () => {
    if (error?.isNetworkError) return 'Network Error'
    if (error?.status) return `Error ${error.status}`
    return 'Error'
  }

  return (
    <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${className}`}>
      <div className="flex items-start">
        <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-medium text-red-800 mb-1">
            {getErrorType()}
          </h3>
          <p className="text-sm text-red-700 mb-3">
            {getErrorMessage()}
          </p>
          {onRetry && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onRetry}
              className="text-red-700 border-red-300 hover:bg-red-100"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Try Again
            </Button>
          )}
        </div>
      </div>
      
      {process.env.NODE_ENV === 'development' && error?.status && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-red-600 hover:text-red-800">
            Debug Info
          </summary>
          <pre className="mt-2 p-2 bg-red-100 rounded text-red-800 overflow-auto">
            {JSON.stringify({
              status: error.status,
              url: error.config?.url,
              method: error.config?.method,
              timestamp: error.timestamp
            }, null, 2)}
          </pre>
        </details>
      )}
    </div>
  )
}

// Loading Error Component for React Query
export function QueryError({ error, refetch }: { error: any, refetch?: () => void }) {
  return (
    <ApiError 
      error={error} 
      onRetry={refetch}
      className="my-4"
    />
  )
}